import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import "@fontsource/roboto"; // default weight 400
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import rootReducer from "./slices";
import { GlobalProvider } from './context/GlobalProvider';
import { set } from 'lodash';
import { setAuthorization } from './helpers/api_helper';

const store = configureStore({ reducer: rootReducer, devTools: true });

/**
 * Bootstrap authentication token
 *
 * - When user comes from client portal, a token is provided in the URL.
 * - Extract token from URL, persist it to localStorage, and mirror it into sessionStorage
 *   so existing auth helpers/hooks continue to work.
 * - If token is not present in URL, fall back to any existing token in localStorage.
 * - (Optional) For local development, you can still use REACT_APP_AUTH_TOKEN as a fallback.
 */
(() => {
  if (typeof window === "undefined") return;

  // When no token is available, send user to internal login page
  const LOGIN_REDIRECT_URL = "/login";
  const url = new URL(window.location.href);
  if (url.pathname === "/login") return;
  const searchParams = url.searchParams;

  // Try to read token from URL query parameters (e.g. /dashboard?token=...)
  let urlToken =
    searchParams.get("token") ||
    searchParams.get("authToken") ||
    searchParams.get("access_token");

  // Fallback: support routes like /dashboard/<token>
  if (!urlToken) {
    const pathParts = url.pathname.split("/").filter(Boolean);
    const dashboardIndex = pathParts.indexOf("dashboard");
    if (dashboardIndex !== -1 && pathParts.length > dashboardIndex + 1) {
      urlToken = pathParts[dashboardIndex + 1];
    }
  }

  let finalToken = null;

  if (urlToken) {
    finalToken = urlToken;
    // Persist token for subsequent navigations
    window.localStorage.setItem("authToken", urlToken);

    //Setting token after in axios after getting token 
    setAuthorization(urlToken);

    url.searchParams.delete("token");
  window.history.replaceState({}, document.title, url.pathname);
  } else {
    // No token in URL, try to reuse previously stored token
    const stored = window.localStorage.getItem("authToken");
    if (stored) {
      finalToken = stored;
    }
  }

  // As a last resort (mainly for local development), allow env token fallback
  // if (!finalToken && process.env.REACT_APP_AUTH_TOKEN) {
  //   finalToken = process.env.REACT_APP_AUTH_TOKEN;
  // }

  if (!finalToken) {
    window.location.href = LOGIN_REDIRECT_URL;
    return;
  }

  // Mirror into sessionStorage so existing auth flow (getLoggedinUser/useProfile/AuthProtected) keeps working
  const authUser = { token: finalToken };
  window.sessionStorage.setItem("authUser", JSON.stringify(authUser));
})();

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <Provider store={store}>
    <React.Fragment>
      <GlobalProvider>
        <BrowserRouter basename={process.env.PUBLIC_URL}>
          <App />
        </BrowserRouter>
      </GlobalProvider>
    </React.Fragment>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();